import type { IndustryPage, IndustryPageContent } from "./types";

const en: IndustryPageContent = {
  eyebrow: "AI agents · Logistics & supply chain",
  h1: "AI agents for logistics in Malaysia & Singapore",
  intro:
    "Logistics runs on thin margins and enormous document volume — every order, delivery note and invoice has to be keyed, matched and cleared without error. We build AI agents on Claude that take the repetitive matching, data-entry and exception work, while your team keeps the judgment calls. Human-in-the-loop by design, so nothing ships without a person who can see and override it.",
  ctaLabel: "Scope an agent for your operation",
  painHeading: "The economics of logistics",
  painPoints: [
    {
      title: "Thin margins, high volume",
      body:
        "Freight, 3PL and forwarding are cents-per-parcel businesses. The profit isn't in any single shipment — it's in moving huge volume cleanly. So the moment someone re-keys an order into the TMS, chases a wrong address, or reworks a mis-billed consignment, that margin is gone. Manual handling doesn't just cost time; at scale it quietly erases the operation's whole return.",
    },
    {
      title: "Document-matching everywhere",
      body:
        "A single shipment throws off a purchase order, a delivery order, a packing list, a customs declaration and an invoice — and they all have to agree. Three-way PO/DO/invoice matching is where overpayments, duplicate billing and supplier disputes hide. Do it by eye across thousands of lines a week and mismatches slip through as real money lost and delayed releases.",
    },
    {
      title: "Cross-border complexity",
      body:
        "The Malaysia–Singapore trade lane means two customs regimes at once — Kastam on one side, Singapore Customs on the other — plus SST in Malaysia and GST in Singapore. HS codes, permits and values have to line up on both declarations. One inconsistent field and the consignment sits at the checkpoint while demurrage and detention charges climb.",
    },
    {
      title: "Exception-heavy and always-on",
      body:
        "Orders, tracking queries and rate requests don't respect office hours — they arrive overnight, on weekends and in three languages. A held shipment or an unanswered customer message at 11pm is business lost to whoever replied first. Exceptions are the norm, not the edge case, and slow handling is what customers actually notice and remember.",
    },
  ],
  useCasesHeading: "Where agents pay off in logistics",
  useCasesIntro:
    "The highest-return work is the repetitive, rules-heavy flow: order processing into your TMS, invoice and three-way matching, contract and rate-card review, first-line support triage, and an internal knowledge agent that answers your team's SOP and customs questions.",
  sections: [
    {
      heading: "Why agents fit this sector",
      body:
        "Logistics work is structured, high-volume and auditable — which is exactly where a well-scoped agent earns its keep. The engineering matters more than the demo: the goal is a system you can trust to run unattended on the easy cases and escalate the rest.",
      bullets: [
        "Idempotent writes — a retry or a duplicate message can't book the same order or release the same payment twice.",
        "Exceptions routed to a human queue with the context attached, instead of being silently guessed at.",
        "PDPA- and PDPC-aligned handling of shipper, consignee and customer data on both sides of the border.",
        "Every decision logged and auditable, and evals gate the agent before it goes anywhere near production.",
      ],
    },
    {
      heading: "Why Anchor Sprint",
      body:
        "We're a Malaysian AI agency and Certified in the Claude Partner Network. We design, build, deploy and run custom agents on the Claude Agent SDK and MCP, wired into your TMS, accounting and customs tooling.",
      bullets: [
        "Built on the Claude Agent SDK + MCP, with human-in-the-loop as the default, not an afterthought.",
        "A public, worked use-case directory and a cost calculator — you can see the pattern before you commit.",
        "Honest buy-vs-build advice: if an off-the-shelf tool already does the job, we'll tell you so.",
      ],
    },
  ],
  faqs: [
    {
      question: "Can it do three-way PO/DO/invoice matching?",
      answer:
        "Yes — three-way matching is one of the clearest fits. The agent lines up purchase order, delivery order and invoice line by line, clears the ones that agree within your tolerances, and flags every mismatch (quantity, price, missing document) to a human for a decision. It won't paper over a discrepancy to keep the queue moving.",
    },
    {
      question: "Does it handle cross-border Malaysia–Singapore customs documents?",
      answer:
        "It's built for that lane. The agent reads and cross-checks the documents behind both declarations — HS codes, values, permits and party details — so the Kastam and Singapore Customs paperwork stays consistent, and SST versus GST is applied on the right side. Anything ambiguous or high-risk is escalated rather than filed blind.",
    },
    {
      question: "Will it auto-approve payments?",
      answer:
        "No. The agent matches, validates and prepares, but it does not release payments on its own. It surfaces clean items for a fast approval and flags exceptions with the reason attached, so a person always holds the final approval. That control point is deliberate — it's what keeps duplicate and erroneous payments out.",
    },
    {
      question: "Does it work in Bahasa Malaysia, English and Chinese?",
      answer:
        "Yes. Built on Claude, the agents handle Bahasa Malaysia, English and Chinese across documents and customer messages — which matters for support triage and order intake on both sides of the Causeway, where all three show up daily.",
    },
  ],
  ctaTitle: "Re-keying orders and invoices by hand?",
  ctaBody:
    "Tell us your document volumes and where the rework piles up, and we'll say honestly whether an agent is worth building for your operation — or whether an off-the-shelf tool gets you there faster.",
};

const ms: IndustryPageContent = {
  eyebrow: "AI agents · Logistik & rantaian bekalan",
  h1: "AI agents untuk logistik di Malaysia & Singapura",
  intro:
    "Logistik berjalan atas margin yang nipis dan jumlah dokumen yang besar — setiap pesanan, nota penghantaran dan invois perlu dimasukkan, dipadankan dan dilepaskan tanpa ralat. Kami membina AI agents di atas Claude yang mengambil alih kerja padanan, kemasukan data dan pengecualian yang berulang, sementara pasukan anda kekal membuat keputusan pertimbangan. Direka dengan human-in-the-loop, jadi tiada apa yang keluar tanpa seorang yang boleh melihat dan mengatasinya.",
  ctaLabel: "Rangka agent untuk operasi anda",
  painHeading: "Ekonomi logistik",
  painPoints: [
    {
      title: "Margin nipis, jumlah tinggi",
      body:
        "Pengangkutan, 3PL dan penghantaran ialah perniagaan sen-per-bungkusan. Keuntungan bukan pada satu penghantaran — ia pada menggerakkan jumlah besar dengan bersih. Jadi saat seseorang memasukkan semula pesanan ke dalam TMS, mengejar alamat salah, atau membuat semula konsainan yang tersalah bil, margin itu lenyap. Pengendalian manual bukan sekadar memakan masa; pada skala besar ia senyap-senyap memadam seluruh pulangan operasi.",
    },
    {
      title: "Padanan dokumen di mana-mana",
      body:
        "Satu penghantaran menghasilkan purchase order, delivery order, senarai bungkusan, perisytiharan kastam dan invois — dan semuanya perlu sepadan. Padanan tiga hala PO/DO/invois ialah tempat lebihan bayaran, bil berganda dan pertikaian pembekal bersembunyi. Buat dengan mata kasar merentas ribuan baris seminggu dan ketidakpadanan terlepas sebagai wang sebenar yang hilang serta pelepasan yang tertangguh.",
    },
    {
      title: "Kerumitan rentas sempadan",
      body:
        "Laluan dagang Malaysia–Singapura bermakna dua rejim kastam serentak — Kastam di satu pihak, Singapore Customs di pihak lain — serta SST di Malaysia dan GST di Singapura. Kod HS, permit dan nilai perlu selari pada kedua-dua perisytiharan. Satu medan tidak konsisten dan konsainan tersadai di pos pemeriksaan sementara caj demurrage dan detention meningkat.",
    },
    {
      title: "Banyak pengecualian dan sentiasa aktif",
      body:
        "Pesanan, pertanyaan penjejakan dan permintaan kadar tidak menghormati waktu pejabat — ia tiba pada waktu malam, hujung minggu dan dalam tiga bahasa. Penghantaran tersekat atau mesej pelanggan tidak dijawab pada jam 11 malam ialah perniagaan yang hilang kepada sesiapa yang membalas dahulu. Pengecualian ialah norma, bukan kes tepi, dan pengendalian yang lambat itulah yang pelanggan sebenarnya perasan dan ingat.",
    },
  ],
  useCasesHeading: "Di mana agents membawa pulangan dalam logistik",
  useCasesIntro:
    "Kerja pulangan tertinggi ialah aliran berulang dan berat dengan peraturan: pemprosesan pesanan ke dalam TMS anda, padanan invois dan tiga hala, semakan kontrak dan kad kadar, triage sokongan barisan pertama, serta internal knowledge agent yang menjawab soalan SOP dan kastam pasukan anda.",
  sections: [
    {
      heading: "Mengapa agents sesuai untuk sektor ini",
      body:
        "Kerja logistik berstruktur, berjumlah tinggi dan boleh diaudit — itulah tempat agent yang dirangka dengan baik memberi nilai. Kejuruteraannya lebih penting daripada demo: matlamatnya ialah sistem yang boleh anda percayai untuk berjalan tanpa pengawasan pada kes mudah dan menaikkan selebihnya.",
      bullets: [
        "Tulisan idempotent — cubaan semula atau mesej berganda tidak boleh menempah pesanan sama atau melepaskan bayaran sama dua kali.",
        "Pengecualian dihalakan ke barisan manusia dengan konteks disertakan, bukan diteka secara senyap.",
        "Pengendalian data penghantar, penerima dan pelanggan yang selari dengan PDPA dan PDPC di kedua-dua pihak sempadan.",
        "Setiap keputusan dilog dan boleh diaudit, dan evals menapis agent sebelum ia menghampiri produksi.",
      ],
    },
    {
      heading: "Mengapa Anchor Sprint",
      body:
        "Kami sebuah agensi AI Malaysia dan diperakui dalam Claude Partner Network. Kami mereka, membina, melaksana dan menjalankan agents tersuai di atas Claude Agent SDK dan MCP, disambungkan ke TMS, perakaunan dan alat kastam anda.",
      bullets: [
        "Dibina di atas Claude Agent SDK + MCP, dengan human-in-the-loop sebagai lalai, bukan tambahan kemudian.",
        "Direktori kes penggunaan awam yang lengkap dan cost calculator — anda boleh lihat corak sebelum komited.",
        "Nasihat buy-vs-build yang jujur: jika alat siap sedia sudah melakukan kerja itu, kami akan beritahu anda.",
      ],
    },
  ],
  faqs: [
    {
      question: "Boleh ia buat padanan tiga hala PO/DO/invois?",
      answer:
        "Boleh — padanan tiga hala ialah salah satu padanan paling jelas. Agent menyelaraskan purchase order, delivery order dan invois baris demi baris, melepaskan yang sepadan dalam toleransi anda, dan menandakan setiap ketidakpadanan (kuantiti, harga, dokumen hilang) kepada manusia untuk keputusan. Ia tidak akan menutup percanggahan demi mempercepatkan barisan.",
    },
    {
      question: "Adakah ia mengendalikan dokumen kastam rentas sempadan Malaysia–Singapura?",
      answer:
        "Ia dibina untuk laluan itu. Agent membaca dan menyemak silang dokumen di sebalik kedua-dua perisytiharan — kod HS, nilai, permit dan butiran pihak — supaya kertas kerja Kastam dan Singapore Customs kekal konsisten, dan SST berbanding GST dikenakan di pihak yang betul. Apa-apa yang kabur atau berisiko tinggi dinaikkan, bukan difailkan secara membuta.",
    },
    {
      question: "Adakah ia akan meluluskan bayaran secara automatik?",
      answer:
        "Tidak. Agent memadan, mengesahkan dan menyediakan, tetapi ia tidak melepaskan bayaran sendiri. Ia mengetengahkan item bersih untuk kelulusan pantas dan menandakan pengecualian dengan sebab disertakan, jadi seorang sentiasa memegang kelulusan akhir. Titik kawalan itu disengajakan — itulah yang menghalang bayaran berganda dan tersilap.",
    },
    {
      question: "Adakah ia berfungsi dalam Bahasa Malaysia, English dan Bahasa Cina?",
      answer:
        "Ya. Dibina di atas Claude, agents mengendalikan Bahasa Malaysia, English dan Bahasa Cina merentas dokumen dan mesej pelanggan — yang penting untuk triage sokongan dan pengambilan pesanan di kedua-dua pihak Tambak, di mana ketiga-tiganya muncul setiap hari.",
    },
  ],
  ctaTitle: "Masih memasukkan pesanan dan invois dengan tangan?",
  ctaBody:
    "Beritahu kami jumlah dokumen anda dan di mana kerja semula bertimbun, dan kami akan beritahu dengan jujur sama ada agent berbaloi dibina untuk operasi anda — atau sama ada alat siap sedia membawa anda ke sana lebih cepat.",
};

const zh: IndustryPageContent = {
  eyebrow: "AI agents · 物流与供应链",
  h1: "马来西亚与新加坡的物流 AI agents",
  intro:
    "物流依靠微薄的利润和庞大的文件量运作——每一张订单、送货单和发票都必须无误地录入、匹配和清关。我们在 Claude 上构建 AI agents，承担重复的匹配、数据录入和异常处理工作，而判断决策仍由您的团队掌握。设计上采用 human-in-the-loop，因此任何环节在有人能查看并覆写之前都不会自动完成。",
  ctaLabel: "为您的运营规划一个 agent",
  painHeading: "物流的经济学",
  painPoints: [
    {
      title: "利润微薄、量大",
      body:
        "货运、3PL 与货代都是以「每件几分钱」为单位的生意。利润不在于任何一次运输，而在于干净地处理巨大的量。因此，一旦有人把订单重新录入 TMS、追查错误地址，或重做一票开错账单的货，这份利润就没了。人工处理不只是耗时；在规模之下，它会悄悄抹掉整个运营的回报。",
    },
    {
      title: "处处都是文件匹配",
      body:
        "一票货就会产生 purchase order、delivery order、装箱单、报关单和发票——而它们必须相互一致。PO/DO/发票三方匹配正是超额付款、重复开票和供应商纠纷藏身之处。每周用肉眼核对成千上万行，错配就会作为真金白银的损失和延误的放行溜过去。",
    },
    {
      title: "跨境的复杂性",
      body:
        "马来西亚—新加坡贸易通道意味着同时面对两套海关制度——一边是 Kastam，一边是 Singapore Customs——外加马来西亚的 SST 和新加坡的 GST。HS 编码、准证和货值必须在两份报关单上对得上。只要有一个字段不一致，货就卡在关卡，而 demurrage 和 detention 费用不断上涨。",
    },
    {
      title: "异常繁多、全天候",
      body:
        "订单、追踪查询和报价请求不看办公时间——它们在深夜、周末、以三种语言到来。一票被扣的货，或凌晨 11 点无人回复的客户消息，就是输给最先回复者的生意。异常是常态，而非边缘情况，而处理缓慢正是客户真正会注意并记住的。",
    },
  ],
  useCasesHeading: "agents 在物流中带来回报之处",
  useCasesIntro:
    "回报最高的是那些重复、规则密集的流程：订单录入 TMS、发票与三方匹配、合同与费率卡审查、一线客服分流，以及一个回答团队 SOP 与海关问题的内部知识 agent。",
  sections: [
    {
      heading: "为什么 agents 适合这个行业",
      body:
        "物流工作结构化、量大且可审计——这正是一个范围界定良好的 agent 发挥价值之处。工程实现比演示更重要：目标是一套您能放心让它在简单情形下无人值守运行、并把其余情形上报的系统。",
      bullets: [
        "幂等写入——重试或重复消息不会把同一张订单下两次，或把同一笔付款放行两次。",
        "异常连同上下文一并路由到人工队列，而不是被悄悄猜测处理。",
        "对发货人、收货人和客户数据的处理符合 PDPA 与 PDPC，在边境两侧皆然。",
        "每个决策都记录在案、可审计，且 evals 会在 agent 接近生产环境之前先行把关。",
      ],
    },
    {
      heading: "为什么选择 Anchor Sprint",
      body:
        "我们是一家马来西亚 AI 机构，也是 Claude Partner Network 认证合作伙伴。我们在 Claude Agent SDK 与 MCP 上设计、构建、部署并运营定制 agents，接入您的 TMS、财务与海关工具。",
      bullets: [
        "构建于 Claude Agent SDK + MCP 之上，以 human-in-the-loop 为默认，而非事后补上。",
        "公开的、有完整示例的用例目录和一个成本计算器——在您承诺之前即可看到模式。",
        "诚实的 buy-vs-build 建议：如果现成工具已能胜任，我们会如实相告。",
      ],
    },
  ],
  faqs: [
    {
      question: "它能做 PO/DO/发票三方匹配吗？",
      answer:
        "能——三方匹配是最清晰的适用场景之一。agent 逐行对齐 purchase order、delivery order 和发票，在您的容差范围内放行一致的项目，并把每一处不匹配（数量、价格、缺失文件）标记给人工决策。它不会为了让队列往前走而掩盖差异。",
    },
    {
      question: "它能处理马来西亚—新加坡的跨境海关文件吗？",
      answer:
        "它正是为这条通道打造的。agent 读取并交叉核对两份报关单背后的文件——HS 编码、货值、准证和当事方信息——让 Kastam 与 Singapore Customs 的文书保持一致，并让 SST 与 GST 用在正确的一侧。任何含糊或高风险的情形都会上报，而非盲目归档。",
    },
    {
      question: "它会自动批准付款吗？",
      answer:
        "不会。agent 负责匹配、校验和准备，但不会自行放行付款。它会呈现干净的项目供快速批准，并把异常连同原因一并标记，因此最终批准始终掌握在人手中。这个控制点是刻意设计的——正是它把重复和错误付款挡在门外。",
    },
    {
      question: "它支持 Bahasa Malaysia、English 和中文吗？",
      answer:
        "支持。构建于 Claude 之上，agents 能在文件和客户消息中处理 Bahasa Malaysia、English 和中文——这对长堤两侧的客服分流和订单录入很重要，因为这三种语言每天都会出现。",
    },
  ],
  ctaTitle: "还在用手工重录订单和发票？",
  ctaBody:
    "把您的文件量和返工集中的环节告诉我们，我们会如实判断：为您的运营构建一个 agent 是否值得——还是现成工具能更快带您到达目标。",
};

export const logisticsIndustry: IndustryPage = {
  key: "logistics",
  route: "solutions/ai-agents-for-logistics",
  heroImage: "/images/industries/logistics-hero.jpg",
  cardLabel: { en: "Logistics & supply chain", ms: "Logistik & rantaian bekalan", zh: "物流与供应链" },
  useCaseSlugs: [
    "order-processing",
    "invoice-matching",
    "contract-review",
    "support-triage",
    "knowledge-agent",
  ],
  content: { en, ms, zh },
};
